import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Loader2, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import CaseStudyForm from './admin/CaseStudyForm';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const CaseStudiesAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout } = useAdminAuth();
  const [caseStudies, setCaseStudies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStudy, setEditingStudy] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const defaultFormData = {
    company: '', industry_en: '', industry_tr: '', heroImage: '',
    challenge_en: '', challenge_tr: '', solution_en: '', solution_tr: '',
    results: [{ metric: '', label_en: '', label_tr: '' }],
    testimonial: { quote_en: '', quote_tr: '', author: '', role_en: '', role_tr: '' },
    timeline_en: '', timeline_tr: '', participants_en: '', participants_tr: '',
    programs_en: [''], programs_tr: [''], featured: false, status: 'published'
  };
  
  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    if (isAuth) fetchCaseStudies();
  }, [isAuth]);

  const fetchCaseStudies = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl("case-studies/admin"));
      if (response.ok) setCaseStudies(await response.json());
    } catch (error) {
      logger.error('Error fetching case studies:', error);
      toast.error('Failed to load case studies');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a valid image file'); return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image size must be less than 5MB'); return; }

    setIsUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await fetch(apiUrl("upload"), { method: 'POST', body: fd });
      if (response.ok) {
        const data = await response.json();
        const imageUrl = `${API_URL}${data.url}`;
        setFormData(prev => ({ ...prev, heroImage: imageUrl }));
        setImagePreview(imageUrl);
        toast.success('Image uploaded successfully!');
      } else {
        toast.error((await response.json()).detail || 'Failed to upload image');
      }
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingStudy(null);
    setShowForm(false);
    setActiveTab('en');
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEdit = (study) => {
    setFormData({
      company: study.company || '', industry_en: study.industry_en || '', industry_tr: study.industry_tr || '',
      heroImage: study.heroImage || '', challenge_en: study.challenge_en || '', challenge_tr: study.challenge_tr || '',
      solution_en: study.solution_en || '', solution_tr: study.solution_tr || '',
      results: study.results?.length > 0 ? study.results : [{ metric: '', label_en: '', label_tr: '' }],
      testimonial: study.testimonial || { quote_en: '', quote_tr: '', author: '', role_en: '', role_tr: '' },
      timeline_en: study.timeline_en || '', timeline_tr: study.timeline_tr || '',
      participants_en: study.participants_en || '', participants_tr: study.participants_tr || '',
      programs_en: study.programs_en?.length > 0 ? study.programs_en : [''],
      programs_tr: study.programs_tr?.length > 0 ? study.programs_tr : [''],
      featured: study.featured || false, status: study.status || 'published'
    });
    setImagePreview(study.heroImage);
    setEditingStudy(study);
    setShowForm(true);
  };

  const handleSubmit = async (e, submitStatus = null) => {
    e.preventDefault();
    if (!formData.challenge_en || formData.challenge_en.trim().length < 50) {
      toast.error('English challenge description must be at least 50 characters'); return;
    }
    if (!formData.solution_en || formData.solution_en.trim().length < 50) {
      toast.error('English solution description must be at least 50 characters'); return;
    }

    setIsSaving(true);
    const dataToSubmit = {
      ...formData, status: submitStatus || formData.status,
      programs_en: formData.programs_en.filter(p => p.trim() !== ''),
      programs_tr: formData.programs_tr.filter(p => p.trim() !== ''),
      results: formData.results.filter(r => r.metric.trim() !== '' && r.label_en.trim() !== '')
    };

    try {
      const url = editingStudy ? apiUrl(`case-studies/${editingStudy.id}`) : apiUrl("case-studies");
      const response = await fetch(url, {
        method: editingStudy ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSubmit)
      });
      if (response.ok) {
        toast.success(`Case study ${dataToSubmit.status === 'draft' ? 'saved as draft' : 'published'} successfully!`);
        resetForm();
        fetchCaseStudies();
      } else {
        toast.error((await response.json()).detail || 'Failed to save case study');
      }
    } catch (error) {
      logger.error('Error saving case study:', error);
      toast.error('Failed to save case study');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (studyId) => {
    if (!window.confirm('Are you sure you want to delete this case study?')) return;
    try {
      const response = await fetch(apiUrl(`case-studies/${studyId}`), { method: 'DELETE' });
      if (response.ok) { toast.success('Case study deleted!'); fetchCaseStudies(); }
      else toast.error('Failed to delete case study');
    } catch (error) {
      logger.error('Error deleting case study:', error);
      toast.error('Failed to delete case study');
    }
  };

  if (isCheckingAuth) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  if (!isAuth) return <AdminLogin onLogin={handleLogin} title="Case Studies Admin" />;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="case-studies-admin">
      <AdminHeader
        title="Case Studies Admin"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        onAddNew={() => { resetForm(); setShowForm(true); }}
        addNewLabel="New Case Study"
      />

      <div className="container mx-auto px-6 py-8">
        {/* Form Modal */}
        {showForm && (
          <CaseStudyForm
            formData={formData}
            setFormData={setFormData}
            editingStudy={editingStudy}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onSubmit={handleSubmit}
            onClose={resetForm}
            isSaving={isSaving}
            isUploading={isUploading}
            imagePreview={imagePreview}
            setImagePreview={setImagePreview}
            fileInputRef={fileInputRef}
            onImageUpload={handleImageUpload}
          />
        )}

        {/* Case Studies List */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20"><Loader2 className="w-12 h-12 animate-spin text-blue-600" /></div>
        ) : caseStudies.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow-md">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No case studies yet</h3>
            <p className="text-gray-500 mb-6">Create your first case study to get started</p>
            <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Create First Case Study
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Company</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Industry</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Results</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {caseStudies.map((study) => (
                    <tr key={study.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-4">
                          <img src={study.heroImage} alt={study.company} className="w-16 h-12 object-cover rounded-lg" />
                          <div>
                            <h4 className="font-semibold text-gray-900">{study.company}</h4>
                            {study.industry_tr && <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded mt-1">TR available</span>}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6"><span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">{study.industry_en}</span></td>
                      <td className="py-4 px-6 text-gray-600">{study.results?.length || 0} metrics</td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col gap-1">
                          <span className={`px-3 py-1 text-sm font-medium rounded-full inline-block w-fit ${study.status === 'draft' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'}`}>
                            {study.status === 'draft' ? 'Draft' : 'Published'}
                          </span>
                          {study.featured && <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full inline-block w-fit">Featured</span>}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleEdit(study)} className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors" title="Edit"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(study.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Stats */}
        {caseStudies.length > 0 && (
          <div className="mt-8 grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6"><div className="text-3xl font-bold text-gray-900">{caseStudies.length}</div><div className="text-gray-500">Total Studies</div></div>
            <div className="bg-white rounded-xl shadow-md p-6"><div className="text-3xl font-bold text-green-600">{caseStudies.filter(s => s.status !== 'draft').length}</div><div className="text-gray-500">Published</div></div>
            <div className="bg-white rounded-xl shadow-md p-6"><div className="text-3xl font-bold text-gray-500">{caseStudies.filter(s => s.status === 'draft').length}</div><div className="text-gray-500">Drafts</div></div>
            <div className="bg-white rounded-xl shadow-md p-6"><div className="text-3xl font-bold text-yellow-600">{caseStudies.filter(s => s.featured).length}</div><div className="text-gray-500">Featured</div></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseStudiesAdmin;
